import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  ListItemIcon,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Person as PersonIcon,
  Folder as FolderIcon,
  Image as ImageIcon,
  School as SchoolIcon,
  Chat as ChatIcon,
  ArrowBack as ArrowBackIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Forum as ForumIcon,
  HelpCenter as HelpCenterIcon,
} from '@mui/icons-material';
import authService, { UserInfo } from '../services/authService';

const DRAWER_WIDTH_EXPANDED = 260;
const DRAWER_WIDTH_COLLAPSED = 72;

const AdminLayout: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true); // Collapsed by default
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const verifyAuth = async () => {
      const verifiedUser = await authService.verifyToken();
      setUser(verifiedUser);
      setLoading(false);
    };
    verifyAuth();
  }, []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await authService.logout();
    setUser(null);
    navigate('/admin/login');
  };

  const handleBackToChat = () => {
    navigate('/');
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const menuItems = [
    { path: '/admin/franconnect', label: 'FranConnect', icon: <FolderIcon /> },
    { path: '/admin/widen', label: 'Widen Media', icon: <ImageIcon /> },
    { path: '/admin/litmos', label: 'Litmos', icon: <SchoolIcon /> },
    { path: '/admin/zendesk', label: 'Zendesk', icon: <HelpCenterIcon /> },
    { path: '/admin/conversations', label: 'Conversations', icon: <ForumIcon /> },
  ];

  const drawerWidth = sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED;

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#F8FAFC',
        }}
      >
        <CircularProgress sx={{ color: '#00ACDD' }} />
      </Box>
    );
  }

  // Not authenticated - redirect to login
  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Admin Header - White with Blue accent */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: '#FFFFFF',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderBottom: '1px solid #E0E0E0',
        }}
      >
        <Toolbar>
          {/* Toggle Sidebar */}
          <IconButton
            onClick={toggleSidebar}
            sx={{
              mr: 1,
              color: '#64748B',
              '&:hover': { 
                color: '#00ACDD',
                background: 'rgba(0, 172, 221, 0.1)' 
              },
            }}
          >
            {sidebarCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
          </IconButton>

          {/* Back to Chat */}
          <Tooltip title="Back to Chat">
            <IconButton
              onClick={handleBackToChat}
              sx={{
                mr: 2,
                color: '#64748B',
                '&:hover': { 
                  color: '#00ACDD',
                  background: 'rgba(0, 172, 221, 0.1)' 
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>

          {/* Logo */}
          <Box
            component="img"
            src="/burn-new-logo.svg"
            alt="BurnGPT"
            sx={{
              height: 32,
              mr: 2,
            }}
            onError={(e: any) => {
              e.target.style.display = 'none';
            }}
          />
          
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              color: '#1E293B',
              fontFamily: '"Outfit", sans-serif',
            }}
          >
            BurnGPT Admin
          </Typography>

          {/* User Menu */}
          <IconButton
            onClick={handleMenuOpen}
            sx={{
              ml: 2,
              '&:hover': { background: 'rgba(0, 172, 221, 0.1)' },
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                background: 'linear-gradient(135deg, #00ACDD 0%, #0078A0 100%)',
                fontSize: '0.9rem',
                fontWeight: 600,
                fontFamily: '"Outfit", sans-serif',
              }}
            >
              {user.username.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 200,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                background: '#FFFFFF',
                border: '1px solid #E0E0E0',
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#1E293B',
                  fontFamily: '"Outfit", sans-serif',
                }}
              >
                {user.username}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#64748B',
                  fontFamily: '"Outfit", sans-serif',
                }}
              >
                Administrator
              </Typography>
            </Box>
            <Divider />
            <MenuItem 
              onClick={handleMenuClose}
              sx={{ 
                color: '#1E293B',
                '&:hover': { background: '#F1F5F9' },
              }}
            >
              <ListItemIcon>
                <PersonIcon fontSize="small" sx={{ color: '#64748B' }} />
              </ListItemIcon>
              Profile
            </MenuItem>
            <Divider />
            <MenuItem 
              onClick={handleLogout} 
              sx={{ 
                color: '#DC2626',
                '&:hover': { background: 'rgba(220, 38, 38, 0.1)' },
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" sx={{ color: '#DC2626' }} />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Admin Sidebar - Collapsible */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          transition: 'width 0.2s ease-in-out',
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            mt: 8,
            height: 'calc(100vh - 64px)',
            background: '#FAFAFA',
            borderRight: '1px solid #E0E0E0',
            transition: 'width 0.2s ease-in-out',
            overflowX: 'hidden',
          },
        }}
      >
        {!sidebarCollapsed && (
          <Box sx={{ p: 2 }}>
            <Typography
              variant="overline"
              sx={{
                color: '#64748B',
                fontWeight: 600,
                letterSpacing: 1.5,
                fontSize: '0.7rem',
                fontFamily: '"Outfit", sans-serif',
              }}
            >
              Configuration
            </Typography>
          </Box>
        )}
        
        <List sx={{ px: sidebarCollapsed ? 0.5 : 1, mt: sidebarCollapsed ? 1 : 0 }}>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                <Tooltip title={sidebarCollapsed ? item.label : ''} placement="right">
                  <ListItemButton
                    onClick={() => navigate(item.path)}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      px: sidebarCollapsed ? 1.5 : 2,
                      minHeight: 48,
                      justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                      background: isActive ? 'rgba(0, 172, 221, 0.1)' : 'transparent',
                      borderLeft: sidebarCollapsed ? 'none' : (isActive ? '3px solid #00ACDD' : '3px solid transparent'),
                      '&:hover': {
                        background: isActive 
                          ? 'rgba(0, 172, 221, 0.15)' 
                          : '#F1F5F9',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isActive ? '#00ACDD' : '#64748B',
                        minWidth: sidebarCollapsed ? 0 : 40,
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {!sidebarCollapsed && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          sx: {
                            fontWeight: isActive ? 600 : 400,
                            color: isActive ? '#00ACDD' : '#1E293B',
                            fontFamily: '"Outfit", sans-serif',
                            fontSize: '0.95rem',
                          },
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          })}
        </List>

        {/* Back to Chat Button */}
        <Box sx={{ mt: 'auto', p: sidebarCollapsed ? 1 : 2 }}>
          {!sidebarCollapsed && <Divider sx={{ mb: 2 }} />}
          <Tooltip title={sidebarCollapsed ? 'Back to Chat' : ''} placement="right">
            <ListItemButton
              onClick={handleBackToChat}
              sx={{
                borderRadius: 2,
                py: 1.5,
                px: sidebarCollapsed ? 1.5 : 2,
                minHeight: 48,
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                background: '#F1F5F9',
                '&:hover': {
                  background: '#E2E8F0',
                },
              }}
            >
              <ListItemIcon sx={{ color: '#64748B', minWidth: sidebarCollapsed ? 0 : 40, justifyContent: 'center' }}>
                <ChatIcon />
              </ListItemIcon>
              {!sidebarCollapsed && (
                <ListItemText
                  primary="Back to Chat"
                  primaryTypographyProps={{
                    sx: {
                      color: '#1E293B',
                      fontFamily: '"Outfit", sans-serif',
                      fontSize: '0.95rem',
                    },
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </Box>
      </Drawer>

      {/* Main Content - Light background */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: 8,
          background: '#F8FAFC',
          minHeight: 'calc(100vh - 64px)',
          p: { xs: 1, md: 2 },
          transition: 'margin-left 0.2s ease-in-out',
          overflow: 'hidden',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
